import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/cn';

import { EmptyState, ErrorState } from './states';

/**
 * A titled chart with its own three states.
 *
 * Charts are the surface most likely to ship with only a happy path, and a dashboard
 * panel that renders an empty axis when a church has no data yet reads as broken
 * rather than new.
 */

export interface ChartCardProps {
  title: string;
  description?: string;
  /** Period selector, fund filter, export — right-aligned in the header. */
  actions?: ReactNode;
  loading?: boolean;
  error?: { title: string; body?: string; onRetry?: () => void; retryLabel?: string };
  empty?: { title: string; body?: string };
  /** Whether the chart has anything to draw. */
  hasData?: boolean;
  height?: number;
  children: ReactNode;
  /** Title links here. Header actions stay outside the link. */
  to?: string;
  className?: string;
  /** Cap the plot area to `height` instead of growing with content. */
  plotFixed?: boolean;
}

export const ChartCard = ({
  title,
  description,
  actions,
  loading = false,
  error,
  empty,
  hasData = true,
  height = 260,
  children,
  to,
  className,
  plotFixed = false,
}: ChartCardProps) => {
  const heading = to ? (
    <Link
      to={to}
      className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {title}
    </Link>
  ) : (
    title
  );

  const plot = (
    <div
      style={plotFixed ? { height, maxHeight: height } : { minHeight: height }}
      className={cn(
        'flex min-w-0 flex-col',
        plotFixed ? 'justify-start overflow-hidden' : 'justify-center',
      )}
    >
      {loading ? (
        <div className="space-y-2" aria-busy>
          <Skeleton className="h-full w-full" style={{ height: plotFixed ? '100%' : height }} />
        </div>
      ) : error ? (
        <div className={cn('flex w-full', plotFixed && 'h-full items-center justify-center')}>
          <ErrorState
            title={error.title}
            body={error.body}
            onRetry={error.onRetry}
            retryLabel={error.retryLabel}
          />
        </div>
      ) : !hasData && empty ? (
        <div className={cn('flex w-full', plotFixed && 'h-full items-center justify-center')}>
          <EmptyState title={empty.title} body={empty.body} />
        </div>
      ) : (
        <div className="min-w-0 w-full">{children}</div>
      )}
    </div>
  );

  return (
    <Card
      className={cn(
        to && 'transition-colors hover:border-brand/40 hover:bg-surface-sunken/40',
        plotFixed && 'h-full',
        className,
      )}
    >
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle>{heading}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </CardHeader>

      <CardContent className={cn('min-w-0 overflow-hidden', plotFixed && 'flex min-h-0 flex-1 flex-col')}>
        {plot}
      </CardContent>
    </Card>
  );
};
